// Modelo 3D carregado de um GLB exportado do CAD, no mesmo formato de
// contrato.js que transformadorProcedural.js produz.
/* eslint-disable no-param-reassign */
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { srgbColor } from './geometriaHelpers';
import { criarSensorMo2 } from './sensorMo2';
// SPS (sensorSps.js) e BM-2 (sensorBm2.js) desligados por ora — fica para o
// futuro, ver o bloco comentado mais abaixo (criarModeloCad).
// import { criarSensorSps } from './sensorSps';
// import { criarSensorBm2 } from './sensorBm2';
import { buildCurrentBolt } from './transformadorProcedural';

// Sem DRACOLoader, o GLB (compressão Draco obrigatória) falha
// silenciosamente e o slot fica vazio sem indício nenhum.
const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath('/static/draco/');

// Material de reserva, só usado se `materials` não vier em vestirMalha().
const COR = 0x6a6f74;
const RUGOSIDADE = 0.62;

// Nó do GLB -> chave de material em criarMateriais (transformadorProcedural.js).
// Buchas ficam de fora — ver PECAS_BUCHA_CAD, que usa dois materiais por peça.
const PART_MATERIAL_CAD = {
  Body: 'tanque',
  Cooling_Fan1: 'flange',
  Cooling_Fan2: 'flange',
  Online_DGA: 'escuro',
  Top_Oil_Temp: 'chapa',
  Power_pipe_S: 'prateado',
  Power_pipe_M: 'prateado',
  pipe_B: 'prateado',
  Wire_B: 'prateado',
  Wire_M: 'prateado',
  iron_S: 'prateado',
  Nut: 'prateado',
};

// Buchas do GLB são um mesh único (sem separação base/isolador no export) —
// dividirBuchaPorAltura corta a malha em dois grupos de material pela altura.
const PECAS_BUCHA_CAD = [
  'Bushing_B1', 'Bushing_B2', 'Bushing_B3',
  'Bushing_S1', 'Bushing_S2', 'Bushing_S3', 'Bushing_M1',
  'Bushing_mini1', 'Bushing_mini2', 'Bushing_mini3',
];

// Fração da altura da bucha (da base) com material metálico.
const FRACAO_METAL_BUCHA = 0.35;

// Separa os triângulos de uma bucha em dois grupos de material pela altura
// local (ver PECAS_BUCHA_CAD/FRACAO_METAL_BUCHA).
function dividirBuchaPorAltura(geometry, fracaoMetal) {
  const posicao = geometry.getAttribute('position');
  if (!posicao) return false;

  let minY = Infinity;
  let maxY = -Infinity;
  for (let i = 0; i < posicao.count; i += 1) {
    const y = posicao.getY(i);
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  const corte = minY + (maxY - minY) * fracaoMetal;

  const indice = geometry.index;
  const totalTriangulos = indice ? indice.count / 3 : posicao.count / 3;
  const vertice = (t, k) => (indice ? indice.getX(t * 3 + k) : t * 3 + k);

  geometry.clearGroups();
  let grupoAtual = null;
  for (let t = 0; t < totalTriangulos; t += 1) {
    const media = (
      posicao.getY(vertice(t, 0)) + posicao.getY(vertice(t, 1)) + posicao.getY(vertice(t, 2))
    ) / 3;
    const materialIndex = media < corte ? 0 : 1;
    if (grupoAtual && grupoAtual.materialIndex === materialIndex) {
      grupoAtual.count += 3;
    } else {
      grupoAtual = { start: t * 3, count: 3, materialIndex };
      geometry.groups.push(grupoAtual);
    }
  }
  return true;
}

// Buchas que ganham o raio de eletricidade animado (buildCurrentBolt).
const PECAS_ELETRICIDADE_CAD = [
  'Bushing_S1', 'Bushing_S2', 'Bushing_S3', 'Bushing_M1',
  'Bushing_mini1', 'Bushing_mini2', 'Bushing_mini3',
];

// Altura de bucha de referência para BOLT_FROM_Y/BOLT_TO_Y
// (transformadorProcedural.js) — o raio escala pela altura real / este valor.
const RAIO_ALTURA_REFERENCIA = 2.5;

// Vértices processados por fatia do fatiamento entre frames (ver
// vestirMalhaPorEtapas/executarEmFatias) — grande o bastante para não gerar
// fatias demais (overhead de agendar um requestAnimationFrame por fatia),
// pequeno o bastante para uma fatia sozinha não travar um frame inteiro.
const UV_VERTICES_POR_FATIA = 200000;

/**
 * Projeta UV em metros de cena (técnica de aplicarUvDeMundo,
 * cmaAssetTextures.js), num laço só (sem clonar geometria) porque o `Body`
 * sozinho já tem milhões de triângulos.
 *
 * Generator, e não função direta: para uma malha desse tamanho, este laço é
 * o grosso do travamento visual entre o fim do download e o modelo aparecer
 * (ver docs/specs/cma-transformador-cad-performance.md §2). Cada `yield` a
 * cada UV_VERTICES_POR_FATIA vértices é um ponto onde o chamador (ver
 * executarEmFatias) devolve o controle ao navegador para pintar um frame
 * antes de continuar, em vez de calcular a malha inteira de uma vez.
 */
function* aplicarUvEscaladoPorEtapas(geometry, escala) {
  const posicao = geometry.getAttribute('position');
  const normal = geometry.getAttribute('normal');
  if (!posicao || !normal) return;

  const totalVertices = posicao.count;
  const posArray = posicao.array;
  const normArray = normal.array;
  const uv = new Float32Array(totalVertices * 2);

  for (let i = 0; i < totalVertices; i += 1) {
    const indice = i * 3;
    const px = posArray[indice] * escala;
    const py = posArray[indice + 1] * escala;
    const pz = posArray[indice + 2] * escala;
    const nx = Math.abs(normArray[indice]);
    const ny = Math.abs(normArray[indice + 1]);
    const nz = Math.abs(normArray[indice + 2]);

    let u;
    let v;
    if (nx >= ny && nx >= nz) {
      u = pz;
      v = py;
    } else if (ny >= nx && ny >= nz) {
      u = px;
      v = pz;
    } else {
      u = px;
      v = py;
    }
    uv[i * 2] = u;
    uv[i * 2 + 1] = v;

    if ((i + 1) % UV_VERTICES_POR_FATIA === 0) yield;
  }

  const atributo = new THREE.BufferAttribute(uv, 2);
  geometry.setAttribute('uv', atributo);
  geometry.setAttribute('uv2', atributo);
}

// Sem vertexColors o shader dos materiais texturizados lê preto — cor
// branca neutra só para satisfazer o shader.
function aplicarCorNeutra(geometry) {
  if (geometry.getAttribute('color')) return geometry;
  const posicao = geometry.getAttribute('position');
  if (!posicao) return geometry;
  geometry.setAttribute('color', new THREE.BufferAttribute(
    new Float32Array(posicao.count * 3).fill(1), 3,
  ));
  return geometry;
}

// Peça(s) do CAD por âncora de card (mesmos ids de ANCHOR_DEFS em
// transformadorProcedural.js). 'gas' não tem peça no .glb — ganha âncora à
// parte em criarModeloCad, depois de montar o sensor MO-2.
const ANCHORS_CAD = [
  { id: 'buchas', nomes: ['Bushing_B1', 'Bushing_B2', 'Bushing_B3'] },
  { id: 'tensaoCarga', nomes: ['Wire_M'] },
  { id: 'bolsa', nomes: ['Top_Oil_Temp'] },
  {
    id: 'tensao',
    nomes: [
      'Bushing_S1', 'Bushing_S2', 'Bushing_S3', 'Bushing_M1',
      'Bushing_mini1', 'Bushing_mini2', 'Bushing_mini3',
    ],
  },
  { id: 'eletricos', nomes: ['Online_DGA'] },
  { id: 'comutador', nomes: ['Online_DGA'] },
  { id: 'resfriamento', nomes: ['Cooling_Fan1', 'Cooling_Fan2'] },
];

// O menor Y do modelo inteiro é dos canos de dreno, não do corpo — usar o
// corpo como chão evita pendurar o transformador neles.
const PECA_BASE = 'Body';

// Peças que descem abaixo da base no CAD e precisam subir até ela.
const PECAS_PENDENTES = ['pipe_B'];

// Peça em que o sensor MO-2 (sensorMo2.js) fica apoiado.
const PECA_SENSOR = 'Online_DGA';

// Fator de explosão do sensor MO-2 > 1 pra abrir mais que Online_DGA na
// mesma direção, ficando na frente dela.
const SENSOR_EXPLODE_LEAD = 1.3;

// SPS (sensorSps.js) e BM-2 (sensorBm2.js) desligados por ora — fica para o
// futuro, ver o bloco comentado mais abaixo (criarModeloCad) e os imports no
// topo do arquivo.
//
// // Posição ABSOLUTA (metros, local ao `group` do modelo) do controlador SPS
// // — calibrada ao vivo com o painel de debug de CmaAsset3dView.vue (sliders
// // X/Y/Z + "Salvar posição" no console).
// const SPS_POSITION_CAD = new THREE.Vector3(
//   5.578820163226157, 2.580014106692663, -0.586174219593165,
// );
//
// // Distância (metros, ao longo do "lado direito" local do SPS/BM-2, mesmo
// // giro em Y) entre o centro do SPS e o centro do BM-2 — soma das metades
// // das larguras dos dois gabinetes (sensorSps.js, sensorBm2.js) com uma
// // folga visual entre eles. A calibrar ao vivo se o encontro com o SPS
// // ficar apertado ou solto demais.
// const BM2_LATERAL_OFFSET = 0.28;

// Centro e normal da face externa de uma caixa, na direção informada —
// usado pra apoiar o sensor MO-2 na face de Online_DGA.
function faceExterna(caixa, direcao) {
  const eixos = ['x', 'y', 'z'];
  const dominante = eixos.reduce(
    (a, b) => (Math.abs(direcao[b]) > Math.abs(direcao[a]) ? b : a),
  );
  const sinal = direcao[dominante] >= 0 ? 1 : -1;

  const ponto = caixa.getCenter(new THREE.Vector3());
  ponto[dominante] = sinal >= 0 ? caixa.max[dominante] : caixa.min[dominante];

  const normal = new THREE.Vector3();
  normal[dominante] = sinal;

  return { ponto, normal };
}

// Direção de explosão de cada peça nomeada do GLB (amplitude vem de
// EXPLODE_SCALE, CmaAsset3dView.vue).
const PART_EXPLODE_CAD = {
  Body: [0, 0, 0],
  Cooling_Fan1: [-1, 0, 3],
  Cooling_Fan2: [0, 0, -3],
  Bushing_B1: [1, 2, 0],
  Bushing_B2: [1, 2, 0],
  Bushing_B3: [1, 2, 0],
  Bushing_S1: [-1, 2, 0],
  Bushing_S2: [-1, 2, 0],
  Bushing_S3: [-1, 2, 0],
  Bushing_M1: [-1, 2, 0],
  Bushing_mini1: [0, 2, 0],
  Bushing_mini2: [0, 2, 0],
  Bushing_mini3: [0, 2, 0],
  Online_DGA: [3, 0, 1],
  Top_Oil_Temp: [-2, 3, -1],
  Power_pipe_S: [-2, 0, -1],
  Power_pipe_M: [-2, 1, 0],
  iron_S: [0, 1, 0],
  Wire_M: [0, 1, 0],
  Nut: [0, 0, 0],
  Wire_B: [0, -2, 0],
  pipe_B: [0, -2, 1],
};

const PART_NAMES_CAD = Object.keys(PART_EXPLODE_CAD);

function caixaDe(objeto) {
  return new THREE.Box3().setFromObject(objeto);
}

// União das caixas de uma lista de peças — usada pelas âncoras com mais de
// um nó, pra centralizar no meio do grupo em vez do canto da primeira peça.
function caixaDeTodas(objetos) {
  return objetos.reduce((caixa, objeto) => caixa.union(caixaDe(objeto)), new THREE.Box3());
}

function assentarPecasPendentes(malha, baseY) {
  PECAS_PENDENTES.forEach((nome) => {
    const peca = malha.getObjectByName(nome);
    if (!peca) return;
    const desnivel = baseY - caixaDe(peca).min.y;
    if (desnivel > 0) peca.position.y += desnivel;
  });
}

/**
 * Assenta a malha no disco, na escala e orientação da cena. Gira em Y porque
 * o eixo longo do CAD é Z e o do modelo procedural é X.
 *
 * @returns {number} Escala aplicada, para converter cotas do modelo em
 *   unidades de cena.
 */
export function assentarMalha(malha, { alturaAlvo, giroY }) {
  const corpo = malha.getObjectByName(PECA_BASE);
  const baseY = caixaDe(corpo || malha).min.y;
  assentarPecasPendentes(malha, baseY);

  const altura = caixaDe(malha).max.y - baseY;
  const escala = altura > 0 ? alturaAlvo / altura : 1;

  malha.rotation.y = giroY;
  malha.scale.setScalar(escala);
  malha.position.y = -baseY * escala;

  return escala;
}

/**
 * Veste cada peça com o material equivalente da cena (PART_MATERIAL_CAD),
 * mesmas instâncias de criarMateriais (não clones), pra tema e raio de
 * eletricidade valerem sem código extra.
 *
 * Generator, e não função direta: delega o cálculo de UV (o grosso do custo,
 * ver aplicarUvEscaladoPorEtapas) e cede o controle de novo entre uma peça e
 * a próxima — com muitas peças pequenas (buchas, sensores) isso também
 * espalha o custo agregado delas entre frames, não só o do `Body` sozinho.
 */
function* vestirMalhaPorEtapas(malha, envMapIntensity, materials, escala) {
  const reserva = new THREE.MeshStandardMaterial({
    color: srgbColor(COR),
    roughness: RUGOSIDADE,
    metalness: 0,
    envMapIntensity,
  });

  const meshes = [];
  malha.traverse((object) => { if (object.isMesh) meshes.push(object); });

  for (let indice = 0; indice < meshes.length; indice += 1) {
    const object = meshes[indice];
    yield* aplicarUvEscaladoPorEtapas(object.geometry, escala);
    aplicarCorNeutra(object.geometry);

    if (materials && materials.tanque && materials.porcelanaBucha
      && PECAS_BUCHA_CAD.includes(object.name)) {
      dividirBuchaPorAltura(object.geometry, FRACAO_METAL_BUCHA);
      object.material = [materials.tanque, materials.porcelanaBucha];
    } else {
      const material = materials && materials[PART_MATERIAL_CAD[object.name]];
      object.material = material || reserva;
    }

    object.castShadow = true;
    object.receiveShadow = true;

    yield;
  }
}

/**
 * Consome um generator fatiado (ver vestirMalhaPorEtapas) espalhando as
 * fatias entre frames via `requestAnimationFrame`, em vez de esgotá-lo de
 * uma vez — é o que devolve o controle ao navegador para pintar (overlay de
 * carregamento incluso, ver CmaAsset3dLoading.vue) entre uma fatia e outra,
 * no lugar de travar a thread principal do fim do download até o modelo
 * inteiro estar pronto. Ver docs/specs/cma-transformador-cad-performance.md §4.
 */
function executarEmFatias(gerador, aoTerminar) {
  const passo = () => {
    const resultado = gerador.next();
    if (resultado.done) {
      aoTerminar();
      return;
    }
    requestAnimationFrame(passo);
  };
  passo();
}

// Planta um raio de eletricidade animado na base de cada bucha de
// PECAS_ELETRICIDADE_CAD. Filho direto de `group` (não do `partGroup` da
// bucha) pra escalar pela altura real em metros de cena, não pela escala
// embutida na matriz do `partGroup`.
function adicionarRaiosEletricidade(group, parts, materials) {
  if (!materials || !materials.raio) return [];

  group.updateMatrixWorld(true);
  return PECAS_ELETRICIDADE_CAD.map((nome) => {
    const peca = parts[nome];
    if (!peca) return null;

    const caixa = caixaDe(peca);
    const altura = caixa.max.y - caixa.min.y;
    const centro = caixa.getCenter(new THREE.Vector3());
    const base = group.worldToLocal(new THREE.Vector3(centro.x, caixa.min.y, centro.z));

    const bolt = buildCurrentBolt(materials);
    bolt.scale.setScalar(altura > 0 ? altura / RAIO_ALTURA_REFERENCIA : 1);
    bolt.position.copy(base);
    group.add(bolt);
    return bolt;
  }).filter(Boolean);
}

// Ajuste fino, por âncora, da posição do balão de card sobre o centroide de
// construirAncoras — calibração visual, não dado do GLB.
export const ANCHOR_POINT_OFFSET_CAD = {
  buchas: [-0.05, 1.9, -0.8],
  gas: [0, 0, 0], // somado à mão no bloco do sensor MO-2 em criarModeloCad
  tensaoCarga: [0, 0, 0],
  bolsa: [0.1, 1.2, 0],
  tensao: [0.6, 0.5, 1.2],
  eletricos: [1, 0.5, -0.5],
  resfriamento: [0, 0, 0],
};

/**
 * Âncoras de card para o modelo do CAD, no formato de transformadorProcedural.js
 * (só que `part` pode ser uma LISTA de nós, ex.: as 3 buchas de alta).
 *
 * `point` é o centroide dos nós em coordenadas de MUNDO da própria `malha`
 * (sem pai ainda) menos `part.position` — a subtração cancela a soma que
 * `anchorWorldPosition` (CmaAsset3dView.vue) faz depois com a posição do
 * primeiro nó da lista.
 *
 * @param {THREE.Object3D} malha Raiz do GLB, já assentada (assentarMalha) e
 *   ainda sem pai.
 * @returns {Array} `anchors` no formato do contrato.
 */
export function construirAncoras(malha) {
  malha.updateMatrixWorld(true);

  return ANCHORS_CAD.map(({ id, nomes }) => {
    const partes = nomes.map(nome => malha.getObjectByName(nome)).filter(Boolean);
    if (!partes.length) return null;

    const centro = caixaDeTodas(partes).getCenter(new THREE.Vector3());
    const offset = ANCHOR_POINT_OFFSET_CAD[id];
    if (offset) centro.add(new THREE.Vector3().fromArray(offset));

    return {
      id,
      part: partes.length === 1 ? partes[0] : partes,
      point: centro.sub(partes[0].position),
    };
  }).filter(Boolean);
}

/**
 * Separa as peças nomeadas do GLB em `partGroup`s próprios, no formato que
 * transformadorProcedural.js usa para `model.parts` (liga o CAD à mesma
 * explosão genérica de CmaAsset3dView.vue).
 *
 * Reparentar um nó direto herdaria só sua transformação LOCAL, não a matriz
 * de mundo, e a peça saltaria de lugar — por isso decompõe a matriz de MUNDO
 * do nó no `partGroup` antes de zerar o transform local dele.
 *
 * @param {THREE.Object3D} malha Raiz do GLB, já assentada e ainda sem pai.
 * @param {THREE.Group} group Raiz do modelo (espaço de cena).
 * @param {Object} partExplode Ver PART_EXPLODE_CAD.
 * @returns {Object} `parts` no formato do contrato.
 */
export function construirPartes(malha, group, partExplode) {
  malha.updateMatrixWorld(true);
  const parts = {};

  PART_NAMES_CAD.forEach((nome) => {
    const no = malha.getObjectByName(nome);
    if (!no) return;

    const partGroup = new THREE.Group();
    partGroup.name = nome;
    partGroup.matrix.copy(no.matrixWorld);
    partGroup.matrix.decompose(partGroup.position, partGroup.quaternion, partGroup.scale);
    group.add(partGroup);

    partGroup.add(no);
    no.position.set(0, 0, 0);
    no.rotation.set(0, 0, 0);
    no.scale.set(1, 1, 1);

    partGroup.userData.home = partGroup.position.clone();
    partGroup.userData.explode = new THREE.Vector3().fromArray(partExplode[nome] || [0, 0, 0]);

    parts[nome] = partGroup;
  });

  return parts;
}

/*
 * Cache module-level (fora de qualquer instância de componente) da cena
 * "vestida" — depois de assentarMalha (escala/posição) e
 * vestirMalhaPorEtapas (UV/material), mas ANTES de qualquer coisa
 * específica de instância (partes reparentadas, âncoras, sensor MO-2).
 *
 * Existe por dois motivos, os dois formas do mesmo problema — refazer
 * download+parse+Draco+vestirMalha (o grosso do custo de CPU) mais de uma
 * vez para a MESMA malha:
 * 1. As três variantes do carrossel usam a mesma malha (TFMP_MESH, ver
 *    transformer3dModels.js).
 * 2. Um remount do componente Vue que hospeda a cena (ver
 *    docs/specs/cma-loading-3d-diagnostico.md — CmaAsset3dView remonta uma
 *    vez, cedo, quando `CmaDashboard` popula `assetTree`; a causa exata é
 *    de diff de vnode do Vue num ancestral, não deste módulo, então em vez
 *    de caçar aquilo este cache neutraliza o efeito de qualquer remount,
 *    presente ou futuro).
 *
 * Cada chamador CLONA o resultado (`Object3D.clone()`): a clonagem
 * duplica só a hierarquia/transform (Group/Mesh, position/rotation/scale),
 * reaproveitando geometria e material por REFERÊNCIA — é isso que faz a
 * segunda (e enésima) instanciação ser barata.
 */
const cenaVestidaCache = new Map(); // url -> Promise<THREE.Object3D>
const cenaVestidaProgressListeners = new Map(); // url -> Set<function>

function carregarCenaVestida(spec, envMapIntensity, materials, aoProgredir) {
  const { url } = spec.mesh;

  if (aoProgredir) {
    if (!cenaVestidaProgressListeners.has(url)) {
      cenaVestidaProgressListeners.set(url, new Set());
    }
    cenaVestidaProgressListeners.get(url).add(aoProgredir);
  }

  if (!cenaVestidaCache.has(url)) {
    cenaVestidaCache.set(url, new Promise((resolve, reject) => {
      new GLTFLoader()
        .setDRACOLoader(dracoLoader)
        .load(
          url,
          (gltf) => {
            const escala = assentarMalha(gltf.scene, spec.mesh);
            // vestirMalha é o grosso do custo (UV por vértice numa malha de
            // milhões deles) — fatiada entre frames (ver executarEmFatias)
            // para não travar a thread principal do fim do download até a
            // cena-base estar pronta.
            executarEmFatias(
              vestirMalhaPorEtapas(gltf.scene, envMapIntensity, materials, escala),
              () => {
                resolve(gltf.scene);
                cenaVestidaProgressListeners.delete(url);
              },
            );
          },
          (evento) => {
            (cenaVestidaProgressListeners.get(url) || []).forEach(ouvinte => ouvinte(evento));
          },
          reject,
        );
    }));
  }

  return cenaVestidaCache.get(url);
}

/**
 * Cria o modelo do CAD para um slot do carrossel. Devolve o modelo na hora,
 * com o grupo vazio (precisa existir de forma síncrona), e enche o grupo
 * quando o GLB chega.
 *
 * @param {Object} spec Entrada de TRANSFORMER_MODELS (`mesh.url`, `mesh.alturaAlvo`).
 * @param {Object} contexto
 * @param {Object} contexto.materials Materiais da cena (criarMateriais).
 * @param {number} contexto.envMapIntensity Intensidade de ambiente do tema.
 * @param {number} contexto.deslocamentoX Deslocamento do ativo no palco.
 * @param {Function} contexto.seguirVivo Diz se o viewer ainda existe quando o GLB chega.
 * @param {Function} [contexto.aoProgredir] Recebe o evento de progresso do
 *   `GLTFLoader` (`{loaded, total}`) a cada pedaço do `.glb` recebido — ver
 *   docs/specs/cma-loading-3d-diagnostico.md §3.1. `total` vem `0` quando o
 *   servidor não envia `Content-Length`.
 * @param {Function} contexto.aoErrar Recebe a falha de download.
 * @param {Function} contexto.aoMontar Avisa que a malha entrou na cena.
 * @param {boolean} [contexto.adiarCarregamento] Quando `true`, não inicia o
 *   `GLTFLoader` — o modelo volta com `iniciarCarregamento()` para o
 *   chamador disparar depois (ver CmaAsset3dView.vue,
 *   `ensureCadModelLoading`). Três variantes do carrossel usam a MESMA
 *   malha (TFMP_MESH, transformer3dModels.js); montar as três de uma vez
 *   triplica à toa o custo de `GLTFLoader.parse()`+`vestirMalha` (todos
 *   competindo pela mesma thread) antes do slot ativo aparecer.
 * @returns {Object} Modelo no formato de contrato.js.
 */
export function criarModeloCad(spec, {
  materials, envMapIntensity, deslocamentoX, seguirVivo, aoProgredir, aoErrar, aoMontar,
  adiarCarregamento,
}) {
  const group = new THREE.Group();
  group.position.x = deslocamentoX;

  const model = {
    id: spec.id,
    group,
    parts: {},
    anchors: [],
    helices: [],
    ponteiros: [],
    raios: [],
  };

  // Resto do encaixe na cena (âncoras, peças, sensor, `aoMontar`) — roda em
  // cima da CLONE de instância da cena-base já vestida (ver
  // carregarCenaVestida/iniciarCarregamento) — material e UV já prontos,
  // compartilhados por referência com qualquer outra instância da mesma
  // malha; só a hierarquia/transform daqui pra baixo é exclusiva desta.
  function montarNaCena(cena) {
    if (!seguirVivo()) return; // viewer pode ter sido destruído durante o fatiamento/clone

    // Medido antes de group.add: matrixWorld aqui ainda não tem o
    // deslocamento do group (ver construirAncoras).
    model.anchors = construirAncoras(cena);
    // construirPartes reparenta os nós — precisa rodar depois das
    // âncoras, que medem os nós no lugar original.
    model.parts = construirPartes(cena, group, PART_EXPLODE_CAD);
    group.add(cena);
    model.raios = adicionarRaiosEletricidade(group, model.parts, materials);

    // Sensor MO-2 (sensorMo2.js), parado, encostado na face externa de
    // Online_DGA — não é peça do GLB, mas ganha a âncora 'gas' abaixo.
    const pecaSensor = model.parts[PECA_SENSOR];
    if (pecaSensor) {
      group.updateMatrixWorld(true);
      const { ponto, normal } = faceExterna(
        caixaDe(pecaSensor),
        pecaSensor.userData.explode,
      );
      const sensor = criarSensorMo2();
      sensor.rotation.y = THREE.MathUtils.degToRad(91); // ângulo absoluto, calibrado ao vivo
      const altura = new THREE.Box3().setFromObject(sensor).max.y;
      const pontoLocal = group.worldToLocal(ponto.add(normal.multiplyScalar(0.05)));
      sensor.position.set(pontoLocal.x, pontoLocal.y - altura, pontoLocal.z);
      group.add(sensor);

      sensor.userData.home = sensor.position.clone();
      sensor.userData.explode = pecaSensor.userData.explode
        .clone()
        .multiplyScalar(SENSOR_EXPLODE_LEAD);
      model.parts.MO2_sensor = sensor;

      // point em coordenadas LOCAIS do sensor: anchorWorldPosition soma
      // direto a part.position sem aplicar a rotação do sensor, então só
      // um deslocamento em Y (invariante ao giro em Y) é seguro.
      const pontoGas = new THREE.Vector3(0, sensor.userData.alturaEtiqueta, 0);
      const offsetGas = ANCHOR_POINT_OFFSET_CAD.gas;
      if (offsetGas) pontoGas.add(new THREE.Vector3().fromArray(offsetGas));
      model.anchors.push({ id: 'gas', part: sensor, point: pontoGas });

      // Controlador SPS (sensorSps.js) e monitor de buchas BM-2
      // (sensorBm2.js) — desligados por ora a pedido de produto, fica
      // para o futuro. Mantido comentado (e não removido) porque a
      // posição do SPS já foi calibrada ao vivo (ver SPS_POSITION_CAD) e
      // a do BM-2 depende dela.
      //
      // const sps = criarSensorSps();
      // sps.rotation.y = sensor.rotation.y;
      // sps.position.copy(SPS_POSITION_CAD);
      // group.add(sps);
      //
      // sps.userData.home = sps.position.clone();
      // sps.userData.explode = sensor.userData.explode.clone();
      // model.parts.SPS_controller = sps;
      //
      // // Monitor de buchas BM-2 (sensorBm2.js), ao lado direito do SPS —
      // // mesmo giro em Y, deslocado ao longo do "lado direito" local do
      // // SPS por BM2_LATERAL_OFFSET.
      // const bm2 = criarSensorBm2();
      // bm2.rotation.y = sps.rotation.y;
      // const ladoSps = new THREE.Vector3(1, 0, 0).applyAxisAngle(
      //   new THREE.Vector3(0, 1, 0), sps.rotation.y,
      // );
      // bm2.position.copy(SPS_POSITION_CAD).addScaledVector(ladoSps, BM2_LATERAL_OFFSET);
      // group.add(bm2);
      //
      // bm2.userData.home = bm2.position.clone();
      // bm2.userData.explode = sensor.userData.explode.clone();
      // model.parts.BM2_monitor = bm2;
    }

    aoMontar();
  }

  function iniciarCarregamento() {
    carregarCenaVestida(spec, envMapIntensity, materials, aoProgredir).then((cenaBase) => {
      if (!seguirVivo()) return; // viewer pode ter sido destruído no meio do caminho

      // clone(): duplica só a hierarquia/transform desta instância —
      // geometria e material continuam compartilhados por referência com
      // a cena-base cacheada e com qualquer outra instância dela (ver
      // carregarCenaVestida).
      montarNaCena(cenaBase.clone());
    }, aoErrar);
  }

  if (adiarCarregamento) {
    model.iniciarCarregamento = iniciarCarregamento;
  } else {
    iniciarCarregamento();
  }

  return model;
}

export default criarModeloCad;
